'use client'

import { useState } from 'react'

import { ArrowUpIcon, SmileyIcon } from '@phosphor-icons/react'
import { Ring2 } from 'ldrs/react'
import { useTranslations } from 'next-intl'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { EmojiPicker } from '@/components/common/emoji'
import { useAuth } from '@/components/providers/auth-provider'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger
} from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useCountFormat } from '@/hooks'
import { Link } from '@/i18n/navigation'
import { useCommentList, useCreateComment } from '@/lib/api'
import { cn } from '@/lib/utils'

import { useWorkContext } from './work-context'

const CommentSkeleton = () => {
  return (
    <li className="flex min-w-0 gap-2.5">
      <Skeleton className="size-10 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </li>
  )
}

const CommentSkeletonList = () => {
  return (
    <ul className="flex flex-col gap-4 px-5">
      {Array.from({ length: 10 }).map((_, index) => (
        <CommentSkeleton key={index} />
      ))}
    </ul>
  )
}

export const Comments = () => {
  const t = useTranslations()
  const { data } = useWorkContext()
  const { list, total, query } = useCommentList(data?.jobId)

  const formatNumber = useCountFormat()

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    onLoadMore: query.fetchNextPage,
    rootMargin: '0px 0px 200px 0px',
    disabled: !query.hasNextPage
  })

  if (!list.length && !query.isLoading) {
    return <div className="flex size-full items-center justify-center">{t('work.noComments')}</div>
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-hidden">
      <span className="px-5 text-sm font-semibold">
        {t('work.replies', { num: formatNumber(total || data?.commentCount || 0) })}
      </span>
      {query.isLoading ? (
        <CommentSkeletonList />
      ) : (
        <ScrollArea
          ref={rootRef}
          className="h-full overflow-y-auto [&_[data-radix-scroll-area-viewport]>div]:!block"
        >
          <ul className="flex flex-col gap-4 px-5">
            {list.map(item => (
              <li key={item.id} className="flex min-w-0 gap-2.5">
                <Link className="shrink-0" href={`https://digen.ai/@${item.nickName}`}>
                  <Avatar className="size-10">
                    <AvatarImage alt={item.nickName} src={item.faceURL} />
                    <AvatarFallback>{item.nickName?.charAt(0)?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <Link
                    className="block truncate text-sm font-semibold"
                    href={`https://digen.ai/@${item.nickName}`}
                  >
                    {item.nickName}
                  </Link>
                  <span className="text-sm font-medium break-words">{item.content}</span>
                </div>
              </li>
            ))}
          </ul>
          <div ref={infiniteRef} className="my-2 flex items-center justify-center">
            {query.hasNextPage && <Ring2 color="white" size={20} stroke={2} />}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}

export const PostComment = () => {
  const { data } = useWorkContext()
  const { checkAuth } = useAuth()
  const [comment, setComment] = useState(() => '')

  const { mutateAsync, isPending } = useCreateComment(data?.jobId, 20)

  const handleSubmit = async () => {
    await checkAuth()
    await mutateAsync({
      jobId: data?.jobId || '',
      content: comment
    })
    setComment('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleEmojiSelect = (emoji: string) => {
    setComment(prev => prev + emoji)
  }

  return (
    <div className="px-5">
      <div className="flex flex-shrink-0 items-center overflow-hidden rounded-full border border-solid border-white/6 bg-light-800 px-2">
        <EmojiPicker disabled={isPending} onSelect={handleEmojiSelect}>
          <button className="size-8.5 rounded-full" type="button">
            <SmileyIcon size={20} weight="bold" />
          </button>
        </EmojiPicker>
        <Input
          className="h-12.5 flex-1 border-none bg-transparent px-0 outline-none placeholder:text-white/60"
          disabled={isPending}
          placeholder="Add a comment"
          type="text"
          value={comment}
          onChange={e => setComment(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          className="size-8.5 rounded-full"
          disabled={isPending}
          variant="primary"
          onClick={handleSubmit}
        >
          {isPending ? <Ring2 size={16} stroke={2} /> : <ArrowUpIcon weight="bold" />}
        </Button>
      </div>
    </div>
  )
}

export const DesktopCommentsPanel = ({ className }: { className?: string }) => {
  return (
    <div
      className={cn(
        'rounded-3xl border border-solid border-white/5 bg-quaternary pt-6 pb-5',
        className
      )}
    >
      <div className="flex size-full flex-col gap-6">
        <Comments />
        <PostComment />
      </div>
    </div>
  )
}

export const MobileCommentsPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Drawer>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="z-modal h-full">
        <DrawerTitle className="sr-only">Comments</DrawerTitle>
        <DrawerDescription className="sr-only">Comments</DrawerDescription>
        <Comments />
        <PostComment />
      </DrawerContent>
    </Drawer>
  )
}
